const ADSENSE_HOSTNAME = "pagead2.googlesyndication.com";
const ADSENSE_PATH = "/pagead/js/adsbygoogle.js";
const ADSENSE_CLIENT_ID_PATTERN = /^ca-pub-\d+$/;

export const ADSENSE_GLOBAL_STATUS_ATTRIBUTE = "data-adsense-global-script-status";

export type AdsenseGlobalScriptStatus = "active" | "invalid" | "removed";

export type ParsedAdsenseGlobalScript = {
  src: string;
  clientId: string;
  crossorigin: "anonymous";
};

export function isValidAdsenseClientId(value: string) {
  return ADSENSE_CLIENT_ID_PATTERN.test(value.trim());
}

export function isValidAdsenseScriptUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === ADSENSE_HOSTNAME && url.pathname === ADSENSE_PATH;
  } catch {
    return false;
  }
}

export function buildAdsenseScriptUrl(url: string, clientId: string) {
  const next = new URL(url);

  if (clientId && !next.searchParams.has("client")) {
    next.searchParams.set("client", clientId);
  }

  return next.toString();
}

export function parseAdsenseGlobalScript(value: string): ParsedAdsenseGlobalScript | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^https:\/\//i.test(trimmed)) {
    if (!isValidAdsenseScriptUrl(trimmed)) return null;

    const url = new URL(trimmed);
    const clientId = (url.searchParams.get("client") ?? "").trim();
    if (!isValidAdsenseClientId(clientId)) return null;

    return {
      src: url.toString(),
      clientId,
      crossorigin: "anonymous",
    };
  }

  if (!/^<script[\s\S]*><\/script>$/i.test(trimmed)) {
    return null;
  }

  const srcMatch = trimmed.match(/src=["']([^"']+)["']/i);
  if (!srcMatch) return null;

  const src = srcMatch[1]?.trim() ?? "";
  if (!isValidAdsenseScriptUrl(src)) return null;

  const url = new URL(src);
  const clientId = (url.searchParams.get("client") ?? "").trim();
  if (!isValidAdsenseClientId(clientId)) return null;

  return {
    src: url.toString(),
    clientId,
    crossorigin: "anonymous",
  };
}

export function getAdsenseGlobalScriptStatus(): AdsenseGlobalScriptStatus {
  const rawStatus = document.documentElement.getAttribute(ADSENSE_GLOBAL_STATUS_ATTRIBUTE);
  if (rawStatus === "active" || rawStatus === "invalid" || rawStatus === "removed") {
    return rawStatus;
  }
  return "removed";
}